import { Injectable, signal } from '@angular/core';

export interface TourPreferences {
  tourSkipped: boolean;
  tourCompleted: boolean;
  tourStarted: boolean;
  lastTourDate: string | null;
}

const TOUR_REMIND_LATER_KEY = 'tourRemindLater';
const TOUR_STATUS_STORAGE_KEY = 'tourStatus';

@Injectable({
  providedIn: 'root',
})
export class TourPreferenceService {
  private readonly _preferences = signal<TourPreferences>({
    tourSkipped: false,
    tourCompleted: false,
    tourStarted: false,
    lastTourDate: null,
  });

  readonly preferences = this._preferences.asReadonly();

  private onSync: ((prefs: TourPreferences) => Promise<void>) | null = null;

  setSyncCallback(callback: (prefs: TourPreferences) => Promise<void>): void {
    this.onSync = callback;
  }

  initializeFromBackend(tourStatusValue: string | undefined): void {
    const isCompletedFromBackend = tourStatusValue === '1';
    const isCompletedFromLocal = !isCompletedFromBackend && this.readLocalStorage() === '1';

    if (isCompletedFromBackend || isCompletedFromLocal) {
      this._preferences.set({
        tourSkipped: true,
        tourCompleted: true,
        tourStarted: true,
        lastTourDate: null,
      });
    }
  }

  shouldShowWelcomeModal(): boolean {
    const prefs = this._preferences();
    if (prefs.tourSkipped || prefs.tourCompleted || prefs.tourStarted) return false;
    if (this.isRemindLater()) return false;
    return true;
  }

  isFirstTimeUser(): boolean {
    const prefs = this._preferences();
    return !prefs.tourSkipped && !prefs.tourCompleted && !prefs.tourStarted;
  }

  markRemindLater(): void {
    try {
      sessionStorage.setItem(TOUR_REMIND_LATER_KEY, '1');
    } catch {
      // Ignore storage errors
    }
  }

  async markTourSkipped(): Promise<void> {
    const prefs = this._preferences();
    const updatedPrefs: TourPreferences = {
      ...prefs,
      tourSkipped: true,
      lastTourDate: new Date().toISOString(),
    };

    this._preferences.set(updatedPrefs);
    this.clearRemindLater();

    await this.syncPreferencesToBackend(updatedPrefs);
  }

  async markTourStarted(): Promise<void> {
    const prefs = this._preferences();
    const updatedPrefs: TourPreferences = {
      ...prefs,
      tourStarted: true,
      lastTourDate: new Date().toISOString(),
    };

    this._preferences.set(updatedPrefs);
    this.clearRemindLater();

    await this.syncPreferencesToBackend(updatedPrefs);
  }

  async markTourCompleted(): Promise<void> {
    const prefs = this._preferences();
    const updatedPrefs: TourPreferences = {
      ...prefs,
      tourCompleted: true,
      lastTourDate: new Date().toISOString(),
    };

    this._preferences.set(updatedPrefs);
    this.clearRemindLater();

    await this.syncPreferencesToBackend(updatedPrefs);
  }

  resetPreferences(): void {
    const defaultPrefs: TourPreferences = {
      tourSkipped: false,
      tourCompleted: false,
      tourStarted: false,
      lastTourDate: null,
    };

    this._preferences.set(defaultPrefs);
    this.clearRemindLater();
  }

  private isRemindLater(): boolean {
    try {
      return sessionStorage.getItem(TOUR_REMIND_LATER_KEY) === '1';
    } catch {
      return false;
    }
  }

  private clearRemindLater(): void {
    try {
      sessionStorage.removeItem(TOUR_REMIND_LATER_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  private async syncPreferencesToBackend(prefs: TourPreferences): Promise<void> {
    const tourStatus = prefs.tourCompleted || prefs.tourSkipped ? '1' : '0';
    this.writeLocalStorage(tourStatus);

    try {
      await this.onSync?.(prefs);
    } catch {
      // Sync failures are non-critical; localStorage preserves state as fallback
    }
  }

  private readLocalStorage(): string | null {
    try {
      return localStorage.getItem(TOUR_STATUS_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeLocalStorage(value: string): void {
    try {
      localStorage.setItem(TOUR_STATUS_STORAGE_KEY, value);
    } catch {
      // Ignore storage errors (e.g. private browsing)
    }
  }
}
